import React, { useState, useEffect } from 'react';
import {
  Box, IconButton, Tooltip, MenuItem, TextField, FormControl, InputLabel,
  Select, Switch, FormControlLabel, Chip, Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import Datatable from '../../components/Datatable';
import FormDialog from '../../components/Datatable/FormDialog';
import api, { get_one, create, update, delete_one } from '../../services/api';
import { useAuth } from '../../components/AuthProvider';
import { useFormError } from '../../components/Datatable/FormErrorProvider';
import { useNotify } from '../../components/Datatable/NotifyProvider';

const EMPTY = { username: '', name: '', email: '', password: '', role: '', active: 1 };

export default function User() {
  const auth = useAuth();
  const { setErrors } = useFormError();
  const notify = useNotify();
  const [refresh, setRefresh] = useState(false);
  const [form, setForm] = useState({ open: false, id: null });
  const [state, setState] = useState(EMPTY);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    if (!form.open) return;
    setErrors([]);
    api.get('/hermes/user/roles').then((r) => setRoles(r.data || [])).catch(() => setRoles([]));
    if (form.id) {
      get_one('/hermes/user', form.id).then((r) => setState({ ...EMPTY, ...r.data.data, password: '' }));
    } else {
      setState(EMPTY);
    }
  }, [form]);

  const ch = (e) => setState((s) => ({ ...s, [e.target.name]: e.target.value }));

  const submit = () => {
    const payload = { ...state };
    if (form.id && !payload.password) delete payload.password; // não troca senha se vazio
    const req = form.id ? update('/hermes/user', form.id, payload) : create('/hermes/user/create', payload);
    req.then((res) => {
      if (res?.data?.errors) { setErrors(res.data.errors); return; }
      setForm({ open: false, id: null });
      setRefresh((r) => !r);
    }).catch((e) => {
      const msg = e?.response?.data?.detail || 'Erro ao salvar usuário';
      notify.setNotify({ open: true, message: msg, severity: 'error' });
    });
  };

  const remove = (id) => {
    if (!window.confirm('Remover este usuário?')) return;
    delete_one('/hermes/user', id).then((res) => {
      if (res?.data?.error) { window.alert(res.data.error); return; }
      setRefresh((r) => !r);
    });
  };

  const columns = [
    { name: 'id', label: 'ID', options: { display: false, filter: false } },
    { name: 'username', label: 'Usuário' },
    { name: 'name', label: 'Nome' },
    { name: 'email', label: 'E-mail' },
    { name: 'role_id', label: 'role_id', options: { display: false } },
    { name: 'role_name', label: 'Cargo',
      options: { customBodyRender: (v) => <Chip size="small" label={v} variant="outlined" /> } },
    { name: 'active', label: 'Status',
      options: { customBodyRender: (v) => (
        <Chip size="small" label={v ? 'Ativo' : 'Inativo'} color={v ? 'success' : 'default'} />
      ) } },
    {
      name: 'actions', label: 'Ações',
      options: {
        filter: false, sort: false,
        setCellProps: () => ({ align: 'right' }),
        setCellHeaderProps: () => ({ align: 'right' }),
        customBodyRender: (_, meta) => {
          const id = meta.rowData[0];
          const isAdmin = id === 1;
          return (
            <Box display="flex" gap={0.25} justifyContent="flex-end">
              <Tooltip title="Editar">
                <span>
                  <IconButton size="small" disabled={!auth.hasPerm('user.update')}
                              onClick={() => setForm({ open: true, id })}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isAdmin ? 'Usuário admin' : 'Remover'}>
                <span>
                  <IconButton size="small" disabled={!auth.hasPerm('user.delete') || isAdmin}
                              onClick={() => remove(id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];

  return (
    <>
      <FormDialog
        open={form.open}
        title={form.id ? 'Editar usuário' : 'Novo usuário'}
        handlerClose={() => setForm({ open: false, id: null })}
        handlerSubmit={submit}
        btnSubmit={form.id ? 'Atualizar' : 'Salvar'}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField name="username" label="Usuário" fullWidth value={state.username || ''}
                     onChange={ch} disabled={!!form.id} />
          <TextField name="name" label="Nome" fullWidth value={state.name || ''} onChange={ch} />
          <TextField name="email" label="E-mail" type="email" fullWidth value={state.email || ''} onChange={ch} />
          <TextField name="password" type="password" fullWidth
                     label={form.id ? 'Nova senha (deixe em branco p/ manter)' : 'Senha'}
                     value={state.password || ''} onChange={ch} />
          <FormControl fullWidth>
            <InputLabel id="user-role">Cargo</InputLabel>
            <Select labelId="user-role" label="Cargo" value={state.role || ''}
                    onChange={(e) => setState((s) => ({ ...s, role: e.target.value }))}>
              {roles.map((r) => <MenuItem key={r.id} value={r.id}>{r.description}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={!!state.active}
                             onChange={(e) => setState((s) => ({ ...s, active: e.target.checked ? 1 : 0 }))} />}
            label="Ativo"
          />
        </Stack>
      </FormDialog>

      <Datatable
        refresh={refresh}
        handlerRefresh={() => setRefresh((r) => !r)}
        title="Usuários"
        endpoint="/hermes/user"
        options={{
          customToolbar: () => (
            <Tooltip title="Adicionar usuário">
              <IconButton disabled={!auth.hasPerm('user.create')}
                          onClick={() => setForm({ open: true, id: null })}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          ),
          customToolbarSelect: () => '',
        }}
        columns={columns}
      />
    </>
  );
}
